<script setup lang="ts">
import { computed, ref } from 'vue';

import type { AppTabType } from '../appTabs/AppTabs.model';
import AppButton from '../appButton/AppButton.vue';
import AppTabs from '../appTabs/AppTabs.vue';

interface AppStepperProps {
  steps: AppTabType[];
  nextStepButtonText: string;
  lastStepButtonText: string;
}

const props = defineProps<AppStepperProps>();

interface AppStepperEmits {
  (eventName: 'close'): void;
}
const emit = defineEmits<AppStepperEmits>();

const isLastStep = computed<boolean>(
  () => currentStep.value === props.steps.length - 1
);
const currentStep = ref<number>(0);

const currentStepTabKey = computed<string>(() => {
  const key = props.steps[currentStep.value]?.key;

  return key ?? '';
});

function nextStep(): void {
  if (!isLastStep.value) {
    currentStep.value = currentStep.value + 1;
  } else {
    emit('close');
  }
}

function onTabClick(tabKey: string): void {
  const tabIndex = props.steps.findIndex((step) => step.key === tabKey);
  currentStep.value = tabIndex;
}
</script>

<template>
  <div class="stepper">
    <div class="stepper-tabs">
      <AppTabs
        :tabs="props.steps"
        :selected-tab-key="currentStepTabKey"
        @tab-click="onTabClick"
      />
    </div>

    <div class="step-wrapper">
      <template v-for="(_, index) in steps" :key="index">
        <slot :name="'step-' + index" :active="currentStep === index" />
      </template>
    </div>
    <div class="button-wrapper">
      <AppButton
        state="enabled"
        size="medium"
        :wide="true"
        :type="isLastStep ? 'important' : 'primary'"
        @click="nextStep()"
      >
        <span>
          {{ isLastStep ? props.lastStepButtonText : props.nextStepButtonText }}
        </span>
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.stepper {
  display: flex;
  flex-direction: column;
  height: 100%;

  .stepper-tabs {
    padding-left: var(--spacing-16);
    padding-right: var(--spacing-16);
  }

  .step-wrapper {
    flex: 1;
    overflow-x: auto;

    .active {
      height: 100%;
    }
  }

  .button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-24) var(--spacing-48);
    border-top: 1px solid var(--color-neutral-30);
  }
}
</style>
